import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useCare } from '../contexts/CareContext'
import { useToast, humanizeError } from '../components/shared/Toast'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import SpecialistsSection from '../components/profile/SpecialistsSection'
import InvitePeople from '../components/profile/InvitePeople'
import ExternalCaregivers from '../components/profile/ExternalCaregivers'
import MedicationPhotos from '../components/profile/MedicationPhotos'
import AddRecipientSheet from '../components/profile/AddRecipientSheet'
import MedicationExtractor from '../components/medications/MedicationExtractor'
import TaskTemplates from '../components/admin/TaskTemplates'

const COMMON_CONDITIONS = [
  'Dementia', "Alzheimer's", "Parkinson's", 'Diabetes', 'Post-surgical',
  'Stroke', 'Cancer', 'Kidney disease', 'Heart condition', 'Fall risk',
  'Wandering', 'UTI prone', 'Arthritis',
]
const CARE_STAGES = ['early', 'middle', 'late', 'unknown']
const LEGAL = [
  { key: 'has_poa', label: 'Power of attorney (POA)' },
  { key: 'has_representation_agreement', label: 'Representation agreement' },
  { key: 'has_advance_directive', label: 'Advance directive' },
]
const ROLE_LABEL = { admin: 'Admin', family_member: 'Family', caregiver: 'Caregiver', care_recipient: 'Recipient' }

function draftFrom(r) {
  return {
    full_name: r.full_name || '',
    preferred_name: r.preferred_name || '',
    date_of_birth: r.date_of_birth || '',
    care_stage: r.care_stage || 'unknown',
    conditions: r.conditions || [],
    allergies: r.allergies || [],
    emergency_contact_name: r.emergency_contact_name || '',
    emergency_contact_phone: r.emergency_contact_phone || '',
    family_doctor_name: r.family_doctor_name || '',
    family_doctor_phone: r.family_doctor_phone || '',
    has_poa: !!r.has_poa,
    has_representation_agreement: !!r.has_representation_agreement,
    has_advance_directive: !!r.has_advance_directive,
  }
}

export default function ProfilePage() {
  const { user, profile } = useAuth()
  // canManageClinical (admin or family) is deliberately wider than isAdmin.
  // The legal-document flags stay isAdmin-only: the care_recipients UPDATE
  // policy is `my_role(id) = 'admin'`, so a family member's write would be
  // rejected by RLS no matter what the UI allowed.
  const { recipient, role, isAdmin, canManageClinical: canManage, reload } = useCare()
  const [inviteOpen, setInviteOpen] = useState(false)
  const toast = useToast()
  const navigate = useNavigate()

  const [team, setTeam] = useState([])
  const [meds, setMeds] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(null)
  const [saving, setSaving] = useState(false)
  const [allergyInput, setAllergyInput] = useState('')
  const [customCondition, setCustomCondition] = useState('')
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [legalSaving, setLegalSaving] = useState(false)
  const [addRecipientOpen, setAddRecipientOpen] = useState(false)
  const [extractorOpen, setExtractorOpen] = useState(false)

  const load = useCallback(async () => {
    if (!recipient) return
    const [teamRes, medRes] = await Promise.all([
      supabase
        .from('care_team_members')
        .select('id, role, relationship, user_id, invited_name, invited_email, profile:profiles(full_name, email)')
        .eq('care_recipient_id', recipient.id)
        .eq('is_active', true),
      supabase
        .from('medications')
        .select('id, name, dose, timing, purpose')
        .eq('care_recipient_id', recipient.id)
        .eq('is_active', true)
        .order('created_at', { ascending: true }),
    ])
    setTeam(teamRes.data || [])
    setMeds(medRes.data || [])
    setLoading(false)
  }, [recipient])

  useEffect(() => {
    load()
  }, [load])

  function startEdit() {
    setDraft(draftFrom(recipient))
    setEditing(true)
  }
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }))
  const toggleCondition = (c) =>
    setDraft((d) => ({ ...d, conditions: d.conditions.includes(c) ? d.conditions.filter((x) => x !== c) : [...d.conditions, c] }))
  function addCustomCondition() {
    const v = customCondition.trim()
    if (v && !draft.conditions.includes(v)) set('conditions', [...draft.conditions, v])
    setCustomCondition('')
  }
  function addAllergy() {
    const v = allergyInput.trim()
    if (v && !draft.allergies.includes(v)) set('allergies', [...draft.allergies, v])
    setAllergyInput('')
  }

  async function save() {
    if (!draft.full_name.trim()) return toast.error('Name is required.')
    setSaving(true)
    try {
      const { error } = await supabase
        .from('care_recipients')
        .update({
          full_name: draft.full_name.trim(),
          preferred_name: draft.preferred_name.trim() || null,
          date_of_birth: draft.date_of_birth || null,
          care_stage: draft.care_stage,
          conditions: draft.conditions,
          allergies: draft.allergies,
          emergency_contact_name: draft.emergency_contact_name.trim() || null,
          emergency_contact_phone: draft.emergency_contact_phone.trim() || null,
          family_doctor_name: draft.family_doctor_name.trim() || null,
          family_doctor_phone: draft.family_doctor_phone.trim() || null,
          has_poa: draft.has_poa,
          has_representation_agreement: draft.has_representation_agreement,
          has_advance_directive: draft.has_advance_directive,
        })
        .eq('id', recipient.id)
      if (error) throw error
      await reload()
      setEditing(false)
      toast.success('Profile updated.')
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setSaving(false)
    }
  }

  /**
   * Legal document flags save immediately on tap rather than waiting for the
   * Edit → Save round trip. When the form happens to be open the draft is
   * updated too, so a later Save doesn't overwrite the flag with a stale one.
   */
  async function toggleLegal(key, value) {
    if (!isAdmin) return
    if (editing) set(key, value)
    setLegalSaving(true)
    try {
      const { error } = await supabase
        .from('care_recipients')
        .update({ [key]: value })
        .eq('id', recipient.id)
      if (error) throw error
      await reload()
    } catch (err) {
      if (editing) set(key, !value)
      toast.error(humanizeError(err))
    } finally {
      setLegalSaving(false)
    }
  }

  async function onPhoto(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) return toast.error('Please choose an image.')
    if (file.size > 5 * 1024 * 1024) return toast.error('Image must be under 5MB.')
    setUploadingPhoto(true)
    try {
      const ext = file.name.split('.').pop()
      const path = `${recipient.id}/avatar-${Date.now()}.${ext}`
      const { error: upErr } = await supabase.storage.from('avatars').upload(path, file, { upsert: true })
      if (upErr) throw upErr
      const { data } = supabase.storage.from('avatars').getPublicUrl(path)
      const { error: updErr } = await supabase.from('care_recipients').update({ photo_url: data.publicUrl }).eq('id', recipient.id)
      if (updErr) throw updErr
      await reload()
      toast.success('Photo updated.')
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setUploadingPhoto(false)
    }
  }

  async function removeMember(m) {
    if (m.user_id === user.id) return toast.error("You can't remove yourself.")
    if (!window.confirm(`Remove ${m.profile?.full_name || m.invited_name || 'this member'}?`)) return
    const { error } = await supabase.from('care_team_members').update({ is_active: false }).eq('id', m.id)
    if (error) return toast.error(humanizeError(error))
    setTeam((t) => t.filter((x) => x.id !== m.id))
  }

  if (!recipient || loading) return <LoadingSpinner label="Loading profile…" />

  const initial = (recipient.full_name || '?').charAt(0).toUpperCase()

  return (
    <div className="stack">
      <button onClick={() => navigate('/more')} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 600, alignSelf: 'flex-start', padding: 0 }}>
        ← More
      </button>

      {/* Header w/ avatar */}
      <div className="center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <label style={{ cursor: isAdmin ? 'pointer' : 'default', position: 'relative' }}>
          {recipient.photo_url ? (
            <img src={recipient.photo_url} alt={recipient.full_name} style={{ width: 84, height: 84, borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: 84, height: 84, borderRadius: '50%', background: 'var(--teal)', color: '#fff', fontSize: 34, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {uploadingPhoto ? <span className="spinner" /> : initial}
            </div>
          )}
          {isAdmin && (
            <>
              <span style={{ position: 'absolute', bottom: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>📷</span>
              <input type="file" accept="image/*" onChange={onPhoto} style={{ display: 'none' }} />
            </>
          )}
        </label>
        <div>
          <h1 className="title center" style={{ fontSize: 24 }}>{recipient.full_name}</h1>
          {recipient.preferred_name && <p className="subtitle center">"{recipient.preferred_name}"</p>}
        </div>
        {role && <span className="badge" style={{ textTransform: 'capitalize' }}>You are {ROLE_LABEL[role] || role}</span>}
      </div>

      {/* Details */}
      <div className="card stack">
        <div className="spread">
          <div className="section-title">Details</div>
          {isAdmin && !editing && (
            <button className="btn btn-sm btn-secondary" onClick={startEdit}>Edit</button>
          )}
        </div>

        {!editing ? (
          <div className="stack" style={{ gap: 10 }}>
            <Row label="Date of birth" value={recipient.date_of_birth || '—'} />
            <Row label="Care stage" value={recipient.care_stage} cap />
            <div>
              <div className="label">Conditions</div>
              <div className="row wrap mt-4" style={{ gap: 6 }}>
                {(recipient.conditions || []).length ? recipient.conditions.map((c) => <span key={c} className="badge">{c}</span>) : <span className="muted">—</span>}
              </div>
            </div>
            <div>
              <div className="label">Allergies</div>
              <div className="row wrap mt-4" style={{ gap: 6 }}>
                {(recipient.allergies || []).length ? recipient.allergies.map((a) => <span key={a} className="badge badge-danger">{a}</span>) : <span className="muted">—</span>}
              </div>
            </div>
          </div>
        ) : (
          <div className="stack">
            <div className="field"><label className="label">Full name</label><input className="input" value={draft.full_name} onChange={(e) => set('full_name', e.target.value)} /></div>
            <div className="row" style={{ gap: 10 }}>
              <div className="field grow"><label className="label">Preferred name</label><input className="input" value={draft.preferred_name} onChange={(e) => set('preferred_name', e.target.value)} /></div>
              <div className="field grow"><label className="label">Date of birth</label><input type="date" className="input" value={draft.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} /></div>
            </div>
            <div className="field">
              <label className="label">Care stage</label>
              <select className="select" value={draft.care_stage} onChange={(e) => set('care_stage', e.target.value)}>
                {CARE_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="field">
              <label className="label">Conditions</label>
              <div className="row wrap" style={{ gap: 8 }}>
                {[...new Set([...COMMON_CONDITIONS, ...draft.conditions])].map((c) => (
                  <button key={c} type="button" className={`chip ${draft.conditions.includes(c) ? 'chip-on' : ''}`} onClick={() => toggleCondition(c)}>{c}</button>
                ))}
              </div>
              <div className="row mt-8">
                <input className="input" value={customCondition} onChange={(e) => setCustomCondition(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomCondition())} placeholder="Add another" />
                <button type="button" className="btn btn-secondary" onClick={addCustomCondition}>Add</button>
              </div>
            </div>
            <div className="field">
              <label className="label">Allergies</label>
              <div className="row">
                <input className="input" value={allergyInput} onChange={(e) => setAllergyInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addAllergy())} placeholder="e.g. Penicillin" />
                <button type="button" className="btn btn-secondary" onClick={addAllergy}>Add</button>
              </div>
              {draft.allergies.length > 0 && (
                <div className="row wrap mt-8" style={{ gap: 8 }}>
                  {draft.allergies.map((a) => <button key={a} type="button" className="chip chip-on" onClick={() => set('allergies', draft.allergies.filter((x) => x !== a))}>{a} ✕</button>)}
                </div>
              )}
            </div>
            <div className="row" style={{ gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
              <button className="btn grow" onClick={save} disabled={saving}>{saving ? <span className="spinner" /> : 'Save'}</button>
            </div>
          </div>
        )}
      </div>

      {/* Contacts */}
      <div className="card stack" style={{ gap: 10 }}>
        <div className="section-title">Contacts</div>
        {!editing ? (
          <>
            <Row label="Emergency contact" value={recipient.emergency_contact_name ? `${recipient.emergency_contact_name}${recipient.emergency_contact_phone ? ` · ${recipient.emergency_contact_phone}` : ''}` : '—'} />
            <Row label="Family doctor" value={recipient.family_doctor_name ? `${recipient.family_doctor_name}${recipient.family_doctor_phone ? ` · ${recipient.family_doctor_phone}` : ''}` : '—'} />
          </>
        ) : (
          <>
            <div className="field"><label className="label">Emergency contact</label>
              <div className="row" style={{ gap: 8 }}>
                <input className="input grow" value={draft.emergency_contact_name} onChange={(e) => set('emergency_contact_name', e.target.value)} placeholder="Name" />
                <input className="input grow" value={draft.emergency_contact_phone} onChange={(e) => set('emergency_contact_phone', e.target.value)} placeholder="Phone" />
              </div>
            </div>
            <div className="field"><label className="label">Family doctor</label>
              <div className="row" style={{ gap: 8 }}>
                <input className="input grow" value={draft.family_doctor_name} onChange={(e) => set('family_doctor_name', e.target.value)} placeholder="Name" />
                <input className="input grow" value={draft.family_doctor_phone} onChange={(e) => set('family_doctor_phone', e.target.value)} placeholder="Phone" />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Legal documents — toggle and save on the spot.
          These used to be disabled outside edit mode, which read as "the
          checkboxes are broken": tapping did nothing and there was no hint
          that Edit had to be pressed first. */}
      <div className="card stack" style={{ gap: 8 }}>
        <div className="spread">
          <div className="section-title">Legal documents</div>
          {legalSaving && <span className="muted" style={{ fontSize: 12 }}>Saving…</span>}
        </div>
        {LEGAL.map((l) => {
          const checked = editing ? draft[l.key] : recipient[l.key]
          return (
            <label
              key={l.key}
              className="row"
              style={{ gap: 10, cursor: isAdmin ? 'pointer' : 'default', minHeight: 44 }}
            >
              <input
                type="checkbox"
                checked={!!checked}
                disabled={!isAdmin || legalSaving}
                onChange={(e) => toggleLegal(l.key, e.target.checked)}
                style={{ width: 22, height: 22, flexShrink: 0 }}
              />
              <span className="grow">{l.label}</span>
              {!editing && checked && <span className="badge badge-green">On file</span>}
            </label>
          )
        })}
        {!isAdmin && (
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            Only an admin can change these.
          </p>
        )}
      </div>

      {/* Medications (view only) */}
      <div className="card stack" style={{ gap: 8 }}>
        <div className="spread">
          <div className="section-title">Medications</div>
          <button onClick={() => navigate('/medications')} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 600, fontSize: 14 }}>Manage</button>
        </div>
        {meds.length === 0 && (
          <p className="muted" style={{ fontSize: 14, margin: 0 }}>No active medications.</p>
        )}
        <MedicationPhotos
          recipientId={recipient.id}
          meds={meds}
          canManage={canManage}
          onUploaded={load}
        />
        {canManage && (
          <button className="btn btn-ghost btn-block" onClick={() => setExtractorOpen(true)}>
            📷 Upload prescription or scan label →
          </button>
        )}
      </div>

      {/* Care team */}
      <div className="card stack" style={{ gap: 10 }}>
        <div className="section-title">Care team</div>
        {team.map((m) => {
          const name = m.profile?.full_name || m.invited_name || m.invited_email || 'Pending invite'
          const pending = !m.user_id
          return (
            <div key={m.id} className="spread">
              <div>
                <div style={{ fontWeight: 600 }}>{name}{pending && <span className="muted"> (invited)</span>}</div>
                <div className="muted" style={{ fontSize: 13 }}>{m.relationship || ROLE_LABEL[m.role] || m.role}</div>
              </div>
              <div className="row" style={{ gap: 10 }}>
                <span className="badge">{ROLE_LABEL[m.role] || m.role}</span>
                {isAdmin && m.user_id !== user.id && (
                  <button onClick={() => removeMember(m)} style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 13 }}>Remove</button>
                )}
              </div>
            </div>
          )
        })}
        {isAdmin && <button className="btn btn-ghost btn-block" onClick={() => setInviteOpen(true)}>Invite someone</button>}
        <button className="btn btn-ghost btn-block" onClick={() => setAddRecipientOpen(true)}>
          + Care for someone else (add Ma or Pa)
        </button>
      </div>

      {/* Recurring caregiver schedule */}
      <TaskTemplates recipientId={recipient.id} canManage={isAdmin} />

      {/* External caregivers */}
      <ExternalCaregivers
        recipientId={recipient.id}
        canManage={canManage}
        isAdmin={isAdmin}
      />

      {/* Specialists */}
      <SpecialistsSection recipientId={recipient.id} canManage={canManage} />

      {addRecipientOpen && (
        <AddRecipientSheet onCancel={() => setAddRecipientOpen(false)} />
      )}

      {extractorOpen && (
        <div className="modal-scrim" onClick={(e) => e.target === e.currentTarget && setExtractorOpen(false)}>
          <div className="modal-panel">
            <MedicationExtractor
              onCancel={() => setExtractorOpen(false)}
              onDone={() => { setExtractorOpen(false); load() }}
            />
          </div>
        </div>
      )}

      {inviteOpen && (
        <InvitePeople
          recipient={recipient}
          inviterName={profile?.full_name || 'A family member'}
          onCancel={() => setInviteOpen(false)}
          onDone={() => { setInviteOpen(false); load() }}
        />
      )}

      <div style={{ height: 8 }} />
    </div>
  )
}

function Row({ label, value, cap }) {
  return (
    <div className="spread">
      <span className="label">{label}</span>
      <span style={{ textTransform: cap ? 'capitalize' : 'none' }}>{value}</span>
    </div>
  )
}
